import React, { useCallback, useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classnames from 'classnames';
import { Promise } from 'bluebird';

import { useJsData, useJsDataCallback } from '@bingads-webui-cc-react/js-data-rest';
import { useLoggerContext } from '@bingads-webui-cc-react/instrumentation-base';
import { getRoutePathById } from '@bingads-webui-react/with-site-map';

import { IORecommendationMessageBar } from './io-recommendation';
import { IOWarningAccountMessageBar } from './io-warning-account';
import { IOWarningCustomerMessageBar } from './io-warning-customer';
import { IORecommendationTypesToFetch, LogsScenario } from './constants';
import { useStyles } from './styles';
import { navigateToIOPage, getAccountIOWarningProps, getCustomerIOWarningProps, getDismissWarningsInfo, logActionInfos } from './helper';

export function IONotifications({
  uid, cid, aid, history, siteMap, containerClass,
}) {
  const logger = useLoggerContext({ defaultScenarioName: LogsScenario.Default });
  const styles = useStyles();
  const [warningsTypeIdsToDismiss, setWarningsTypeIdsToDismiss] = useState([]);

  const { data: warnings, loading: isWarningsLoading } = useJsData(
    jsData => jsData.UnifiedQueueNotification.findAll({ cid, aid })
    , [cid, aid]
  );

  const { data: recommendations, loading: isRecommendationsLoading } = useJsData(
    jsData => jsData.Recommendation.findAll({
      cid,
      aid,
      types: IORecommendationTypesToFetch,
    }), [cid, aid]
  );

  const IOWarningProps = useMemo(() => (aid > 0 ?
    getAccountIOWarningProps({ warnings }) :
    getCustomerIOWarningProps({ warnings }))
  , [aid, warnings]);

  const recommendationIds = useMemo(() => _.sortBy(_.pluck(recommendations, 'id')), [recommendations]);

  const { execute: dismissWarnings } = useJsDataCallback(
    (jsData) => {
      const { ids, uiDeliveryIds, notificationTypeIds } = getDismissWarningsInfo({ warnings, warningsTypeIdsToDismiss });

      logActionInfos({
        aid, warningsTypeIds: warningsTypeIdsToDismiss, isDismiss: true, logger, uiDeliveryIds,
      });

      return jsData.UnifiedQueueNotification.destroyAll({ uiDeliveryIds, notificationTypeIds }, {
        beforeDestroy: () => {
          _.each(ids, id => jsData.UnifiedQueueNotification.eject(id));
          return Promise.resolve(ids);
        },
      });
    }, [warningsTypeIdsToDismiss, warnings]
  );

  const { execute: dismissRecommendations } = useJsDataCallback(
    (jsData) => {
      logActionInfos({ recommendationIds, isDismiss: true, logger });
      return jsData.Recommendation.destroyAll(undefined, { params: { cid, aid, ids: recommendationIds } });
    }, [cid, aid, recommendationIds]
  );

  const goToIOPage = useCallback(({ iofilter, recommendationIds: recIds, warningsTypeIds } = {}) => {
    logActionInfos({
      recommendationIds: recIds, warningsTypeIds, aid, logger,
    });
    navigateToIOPage({
      uid, cid, aid, iofilter, history, siteMap,
    });
  }, [aid, cid, history, logger, siteMap, uid]);

  useEffect(() => {
    if (!isWarningsLoading && !_.isEmpty(warningsTypeIdsToDismiss)) {
      dismissWarnings();
      setWarningsTypeIdsToDismiss([]);
    }
  }, [dismissWarnings, isWarningsLoading, warningsTypeIdsToDismiss]);

  useEffect(() => {
    if (!isWarningsLoading && !isRecommendationsLoading && (!_.isEmpty(recommendationIds) || _.some(IOWarningProps, value => !!value))) {
      const path = getRoutePathById(siteMap, 'billing');
      logger.trace({
        scenarioName: path === '/billing' ? LogsScenario.BillingSummaryDisplayed : LogsScenario.AccountPerformanceDisplayed,
        message: {
          isAccountLevel: aid > 0,
          ...IOWarningProps,
          recommendationIds,
        },
      });
    }
  // eslint need to be disabled, if not, the dependencies will be auto fill, finally lead to logging more than once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logger, isWarningsLoading, isRecommendationsLoading]);

  return !(isWarningsLoading || isRecommendationsLoading) && (
    <div className={classnames(['io-notifications', styles.Container, containerClass])}>
      {
        aid > 0 ?
        (
          <IOWarningAccountMessageBar
            {...IOWarningProps}
            buttonOnClick={goToIOPage}
            dismissOnClick={setWarningsTypeIdsToDismiss}
          />
        ) : (
          <IOWarningCustomerMessageBar
            {...IOWarningProps}
            buttonOnClick={goToIOPage}
            dismissOnClick={setWarningsTypeIdsToDismiss}
          />
        )
      }
      <IORecommendationMessageBar
        numOfRecommendations={recommendationIds.length}
        recommendationIds={recommendationIds}
        buttonOnClick={goToIOPage}
        dismissOnClick={dismissRecommendations}
      />
    </div>
  );
}

IONotifications.defaultProps = {
  aid: null,
  containerClass: '',
};

IONotifications.propTypes = {
  uid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  aid: PropTypes.number,
  containerClass: PropTypes.string,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
  })).isRequired,
};
